import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  IconButton,
} from '@mui/material';
import {
  Hub as FlowIcon,
  PlayCircle as LiveIcon,
  CompareArrows as DiffIcon,
  Analytics as SliceIcon,
  Science as StudioIcon,
  Security as GateIcon,
  History as HistoryIcon,
  LibraryBooks as LibraryIcon,
  ChecklistRtl as SuitesIcon,
  Refresh as RefreshIcon,
  Launch as LaunchIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import useStudioStore, { normalizeToolId } from '../state/studioStore';
import ContextHistoryPanel from './ContextHistoryPanel';
import Panel from './Panel';
import { API } from '../lib/api';

// Import the actual tool components
import LiveTrial from '../tools/liveTrial';
import Slice from '../tools/slice';
import Diff from '../tools/diff';
import Gate from '../tools/gate';
import RagStudio from '../tools/ragStudio';

const LEFT_WIDTH = '50%';
const RIGHT_WIDTH = '50%';
const MAX_RECENT_SUITES = 5;
const MAX_RECENT_RUNS = 6;

const TOOL_METADATA = {
  live: { label: 'Live Trial', icon: LiveIcon, color: 'success', component: LiveTrial },
  diff: { label: 'Prompt Diff', icon: DiffIcon, color: 'info', component: Diff },
  slices: { label: 'Slice Analysis', icon: SliceIcon, color: 'warning', component: Slice },
  studio: { label: 'RAG Studio', icon: StudioIcon, color: 'secondary', component: RagStudio },
  gate: { label: 'Prompt Ops', icon: GateIcon, color: 'primary', component: Gate },
};

// Compact tool widgets for dashboard
function ToolWidget({ toolId, data, onLaunch }) {
  const tool = TOOL_METADATA[toolId];
  const IconComponent = tool.icon;

  const getToolData = () => {
    switch (toolId) {
      case 'live':
        return {
          status: data.recentRuns?.length > 0 ? 'Active' : 'Ready',
          detail: `${data.recentRuns?.length || 0} runs`,
          action: 'Start Trial'
        };
      case 'slices':
        return {
          status: data.recentRuns?.length > 0 ? 'Data Available' : 'No Data',
          detail: `${data.runCount || 0} runs to analyze`,
          action: 'Analyze'
        };
      case 'diff':
        return {
          status: data.promptCount > 1 ? 'Ready' : 'Need Versions',
          detail: `${data.promptCount || 0} prompts`,
          action: 'Compare'
        };
      case 'gate':
        return {
          status: data.promptCount > 0 ? 'Active' : 'Empty',
          detail: `${data.promptCount || 0} prompts`,
          action: 'Manage'
        };
      case 'studio':
        return {
          status: 'Ready',
          detail: 'RAG tuning',
          action: 'Open Studio'
        };
      default:
        return { status: 'Unknown', detail: '', action: 'Open' };
    }
  };

  const toolData = getToolData();

  return (
    <Card 
      sx={{ 
        height: 120,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        background: (theme) => `${theme.palette.background.paper}50`,
        '&:hover': {
          borderColor: `${tool.color}.main`,
          background: (theme) => `${theme.palette[tool.color].main}08`,
        }
      }}
    >
      <CardHeader
        sx={{ pb: 1 }}
        avatar={<IconComponent color={tool.color} />}
        title={
          <Typography variant="subtitle2" fontWeight={600}>
            {tool.label}
          </Typography>
        }
        action={
          <IconButton size="small" onClick={() => onLaunch(toolId)}>
            <LaunchIcon fontSize="small" />
          </IconButton>
        }
      />
      <CardContent sx={{ pt: 0, pb: '16px !important' }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {toolData.status} • {toolData.detail}
        </Typography>
        <Button 
          size="small" 
          variant="outlined" 
          color={tool.color}
          sx={{ mt: 1, fontSize: '0.7rem' }}
          onClick={() => onLaunch(toolId)}
        >
          {toolData.action}
        </Button>
      </CardContent>
    </Card>
  );
}

function MetricCard({ icon: IconComponent, label, value, helper }) {
  return (
    <Box
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 1,
        background: (theme) => `${theme.palette.background.paper}30`,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        flex: 1,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <IconComponent fontSize="small" color="primary" />
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Stack>
      <Typography variant="h6" fontWeight={600}>
        {value}
      </Typography>
    </Box>
  );
}

function WorkspaceSidebar({ onOpenTool, summary, onRefreshSummary, onSelectSuite, onSelectRun, onNavigateToTool }) {
  const [expandedTool, setExpandedTool] = useState(null); // null means all collapsed

  const handleExpandTool = (toolId) => {
    setExpandedTool(expandedTool === toolId ? null : toolId);
  };

  return (
    <Panel
      title="Studio Dashboard"
      icon={<FlowIcon fontSize="small" />}
      sx={{
        flex: `0 0 ${LEFT_WIDTH}`,
        minWidth: 320,
        height: '100%',
        borderRadius: (theme) => theme.shape.borderRadius * 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        background: (theme) => 
          `linear-gradient(to bottom right, ${theme.palette.background.paper}AA, ${theme.palette.background.paper}55)`,
        backdropFilter: 'blur(8px)',
      }}
      headerSx={{
        px: 2,
        minHeight: 48,
        height: 48,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        background: (theme) => 
          `linear-gradient(135deg, ${theme.palette.background.paper}66, transparent)`,
        flexShrink: 0,
      }}
      contentSx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
      }}
    >
      {/* Quick Stats */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <MetricCard
            icon={LibraryIcon}
            label="Prompts"
            value={summary.promptCount}
            helper=""
          />
          <MetricCard
            icon={HistoryIcon}
            label="Runs"
            value={summary.runCount}
            helper=""
          />
        </Stack>
        <Button 
          size="small" 
          startIcon={<RefreshIcon fontSize="small" />} 
          onClick={onRefreshSummary}
          variant="outlined"
          fullWidth
        >
          Refresh Data
        </Button>
      </Box>

      {/* All Tools Dashboard - each card fills row and expands inline */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Tools
        </Typography>
        <Stack spacing={2}>
          {Object.keys(TOOL_METADATA).map((toolId) => {
            const tool = TOOL_METADATA[toolId];
            const isExpanded = expandedTool === toolId;
            const ToolComponent = tool.component;
            return (
              <Box key={toolId}>
                <Card
                  sx={{
                    width: '100%',
                    boxShadow: isExpanded ? 6 : 2,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    background: (theme) => `${theme.palette.background.paper}50`,
                    transition: 'box-shadow 0.2s',
                  }}
                >
                  <CardHeader
                    avatar={<tool.icon color={tool.color} />}
                    title={<Typography variant="subtitle2" fontWeight={600}>{tool.label}</Typography>}
                    action={
                      <Button
                        size="small"
                        variant={isExpanded ? 'contained' : 'outlined'}
                        color={tool.color}
                        onClick={() => handleExpandTool(toolId)}
                        sx={{ minWidth: 100 }}
                      >
                        {isExpanded ? 'Collapse' : 'Open'}
                      </Button>
                    }
                  />
                  <CardContent sx={{ pt: 0, pb: isExpanded ? 0 : '16px !important' }}>
                    {/* Show tool summary or full tool */}
                    {!isExpanded ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {toolId === 'live' && `${summary.recentRuns?.length || 0} runs`}
                        {toolId === 'slices' && `${summary.runCount || 0} runs to analyze`}
                        {toolId === 'diff' && `${summary.promptCount || 0} prompts`}
                        {toolId === 'gate' && `${summary.promptCount || 0} prompts`}
                        {toolId === 'studio' && 'RAG tuning'}
                      </Typography>
                    ) : (
                      <Box sx={{ width: '100%', py: 2 }}>
                        <ToolComponent />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* Recent Activity */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Recent Activity
        </Typography>
        {summary.lastRun ? (
          <Box
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              p: 1.5,
              mb: 2,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <HistoryIcon fontSize="small" color="action" />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {summary.lastRun.name || summary.lastRun.id || 'Latest Run'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {summary.lastRun.created_at ? new Date(summary.lastRun.created_at).toLocaleString() : 'In progress'}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={(summary.lastRun.status || 'pending').replace(/_/g, ' ')}
                color={summary.lastRun.status === 'completed' ? 'success' : 'default'}
                sx={{ textTransform: 'capitalize' }}
              />
            </Stack>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No recent activity. Start by creating a prompt or running a test.
          </Typography>
        )}
        {/* Quick Actions */}
        <Stack spacing={1}>
          <Button 
            variant="contained" 
            size="small" 
            startIcon={<GateIcon fontSize="small" />}
            onClick={() => handleExpandTool('gate')}
          >
            Create Prompt
          </Button>
          <Button 
            variant="outlined" 
            size="small" 
            startIcon={<LiveIcon fontSize="small" />}
            onClick={() => handleExpandTool('live')}
          >
            Start Live Trial
          </Button>
        </Stack>
      </Box>

      {/* Quick Access to Recent Items */}
      {summary.suites?.length > 0 && (
        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Recent Suites ({summary.suites.length})
          </Typography>
          <Stack spacing={1}>
            {summary.suites.slice(0, 3).map((suite) => (
              <ListItemButton
                key={suite.id || suite.name}
                sx={{
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  py: 0.5,
                  px: 1,
                }}
                onClick={() => onSelectSuite(suite)}
              >
                <ListItemText
                  primary={
                    <Typography variant="caption" fontWeight={600} noWrap>
                      {suite.name || suite.id}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {suite.case_count || suite.cases?.length || 0} cases
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
          </Stack>
        </Box>
      )}
    </Panel>
  );
}

export default function WorkspaceDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTool = useStudioStore((state) => state.activeTool);
  const setActiveTool = useStudioStore((state) => state.setActiveTool);
  const openToolWithContext = useStudioStore((state) => state.openToolWithContext);

  const [summary, setSummary] = useState({
    loading: true,
    error: '',
    promptCount: 0,
    suiteCount: 0,
    runCount: 0,
    suites: [],
    recentRuns: [],
    lastRun: null,
    prompts: [],
    updatedAt: null,
  });

  const loadSummary = useCallback(async () => {
    setSummary((prev) => ({ ...prev, loading: true, error: '' }));

    try {
      const [prompts, suites, runs] = await Promise.all([
        API.prompts.list().catch(() => []),
        API.proof.suites().catch(() => []),
        API.proof.runs().catch(() => []),
      ]);

      const sortedRuns = Array.isArray(runs)
        ? [...runs].sort((a, b) => {
            const aTime = a?.created_at ? new Date(a.created_at).getTime() : 0;
            const bTime = b?.created_at ? new Date(b.created_at).getTime() : 0;
            return bTime - aTime;
          })
        : [];

      setSummary({
        loading: false,
        error: '',
        promptCount: Array.isArray(prompts) ? prompts.length : 0,
        suiteCount: Array.isArray(suites) ? suites.length : 0,
        runCount: Array.isArray(runs) ? runs.length : 0,
        suites: Array.isArray(suites) ? suites : [],
        prompts: Array.isArray(prompts) ? prompts : [],
        recentRuns: sortedRuns.slice(0, MAX_RECENT_RUNS),
        lastRun: sortedRuns[0] || null,
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      setSummary((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'Unable to load workspace summary.',
      }));
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const rawTool = searchParams.get('tool');
  const normalizedTool = useMemo(() => normalizeToolId(rawTool || activeTool || 'live') || 'live', [rawTool, activeTool]);

  useEffect(() => {
    if (normalizedTool !== activeTool) {
      setActiveTool(normalizedTool);
    }
  }, [normalizedTool, activeTool, setActiveTool]);

  useEffect(() => {
    const canonicalTool = normalizeToolId(rawTool || '') || normalizedTool;
    if (!rawTool || canonicalTool !== rawTool) {
      const next = new URLSearchParams(searchParams.toString());
      next.set('tool', canonicalTool || 'live');
      setSearchParams(next, { replace: !rawTool });
    }
  }, [rawTool, normalizedTool, searchParams, setSearchParams]);

  const handleOpenTool = useCallback(
    (toolName, contextData = {}) => {
      const nextTool = normalizeToolId(toolName) || 'live';
      const next = new URLSearchParams(searchParams.toString());
      next.set('tool', nextTool);
      setSearchParams(next, { replace: false });
      openToolWithContext(nextTool, contextData);
      setActiveTool(nextTool);
    },
    [openToolWithContext, searchParams, setActiveTool, setSearchParams]
  );

  const handleNavigateToTool = useCallback((toolName) => {
    const nextTool = normalizeToolId(toolName) || 'live';
    const next = new URLSearchParams(searchParams.toString());
    next.set('tool', nextTool);
    setSearchParams(next, { replace: false });
    setActiveTool(nextTool);
  }, [searchParams, setSearchParams, setActiveTool]);

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: 0, gap: 2, p: 2 }}>
      <WorkspaceSidebar
        onOpenTool={handleOpenTool}
        summary={summary}
        onRefreshSummary={loadSummary}
        onSelectSuite={(suite) => handleOpenTool('live', { suite })}
        onSelectRun={(run) => handleOpenTool('live', { run })}
        onNavigateToTool={handleNavigateToTool}
      />
      <Box
        sx={{
          flex: `0 0 ${RIGHT_WIDTH}`,
          minWidth: 320,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <ContextHistoryPanel
            activeTool={normalizedTool}
            activeCards={[normalizedTool]}
            workspaceSummary={summary}
          />
        </Box>
      </Box>
    </Box>
  );
}
